import { makeStyles, shorthands, tokens } from '@coreai/fluentui-react';
import { ottoTokens } from '@/theme/ottoTokens';

/**
 * GlobalNavPalette styles.
 *
 * Editorial dark-or-light modal with:
 *  - Backdrop blur (uses ottoTokens.overlayBackdrop, matching CommandPalette)
 *  - Slide-up enter animation, with `prefers-reduced-motion` fallback
 *  - High-contrast `forced-colors: active` overrides for the active row
 *  - Token-only colors so light + dark themes both look right
 */
export const usePaletteStyles = makeStyles({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: ottoTokens.overlayBackdrop,
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '14vh',
    zIndex: 10000,
    animationName: {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    animationDuration: tokens.durationFast,
    animationTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      animationDuration: '0.01ms',
    },
  },
  container: {
    width: '600px',
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: '70vh',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow28,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animationName: {
      from: { opacity: 0, transform: 'translateY(-8px) scale(0.98)' },
      to: { opacity: 1, transform: 'translateY(0) scale(1)' },
    },
    animationDuration: tokens.durationNormal,
    animationTimingFunction: tokens.curveDecelerateMid,
    '@media (prefers-reduced-motion: reduce)': {
      animationDuration: '0.01ms',
      animationName: 'none',
    },
    '@media (forced-colors: active)': {
      ...shorthands.borderColor('CanvasText'),
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingBlock: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalL,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  searchIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  input: {
    flex: 1,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground1,
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    minWidth: 0,
    '::placeholder': {
      color: tokens.colorNeutralForeground3,
    },
  },
  shortcutBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    paddingInline: tokens.spacingHorizontalXS,
    paddingBlock: tokens.spacingVerticalXXS,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    fontFamily: tokens.fontFamilyMonospace,
    borderRadius: tokens.borderRadiusSmall,
    flexShrink: 0,
  },
  // Additive style for the interactive "run" affordance: resets native
  // <button> chrome (otherwise dark mode exposes the user-agent border)
  // and adds the hover / focus / cursor states the static "navigate"
  // span shouldn't have.
  shortcutBadgeButton: {
    border: 'none',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
    ':focus-visible': {
      outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '1px',
    },
  },
  results: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingBlock: tokens.spacingVerticalXS,
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalS,
    paddingInline: tokens.spacingHorizontalL,
    paddingBlock: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalM,
  },
  groupHeaderTitle: {
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  groupHeaderSubtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  groupTruncated: {
    paddingInline: tokens.spacingHorizontalL,
    paddingBlock: tokens.spacingVerticalXS,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontStyle: 'italic',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    width: '100%',
    boxSizing: 'border-box',
    paddingInline: tokens.spacingHorizontalL,
    paddingBlock: tokens.spacingVerticalSNudge,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    color: tokens.colorNeutralForeground1,
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase300,
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorSubtleBackgroundHover,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  rowActive: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorNeutralForeground1,
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
      color: 'HighlightText',
    },
  },
  rowIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    display: 'flex',
    // Fluent icons ship with intrinsic dimensions, so we size the SVG
    // directly. The flex wrapper just keeps the glyph centred when its
    // viewBox padding doesn't match exactly.
    '& > svg': {
      width: ottoTokens.paletteRowIconSize,
      height: ottoTokens.paletteRowIconSize,
    },
  },
  rowText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  rowLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  rowHint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: tokens.lineHeightBase200,
  },
  rowCurrentTag: {
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    flexShrink: 0,
    marginLeft: tokens.spacingHorizontalS,
  },
  empty: {
    paddingInline: tokens.spacingHorizontalL,
    paddingBlock: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    textAlign: 'center',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingInline: tokens.spacingHorizontalL,
    paddingBlock: tokens.spacingVerticalS,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  footerKey: {
    fontFamily: tokens.fontFamilyMonospace,
    paddingInline: tokens.spacingHorizontalXS,
    paddingBlock: '1px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusSmall,
    color: tokens.colorNeutralForeground2,
  },
  footerEnd: {
    marginLeft: 'auto',
  },
  // ── Extras: workflow actions and Ask AI prompt rows ──────────────────
  // Reused row chrome (`row`/`rowIcon`/`rowActive`) gives Actions and Ask
  // AI the same visual rhythm as the nav rows above; only a couple of
  // helpers are unique to the extras.
  closeButton: {
    paddingInline: tokens.spacingHorizontalXS,
  },
  rowShortcut: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    paddingInline: tokens.spacingHorizontalXS,
    paddingBlock: tokens.spacingVerticalXXS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusSmall,
    flexShrink: 0,
    marginLeft: tokens.spacingHorizontalS,
  },
});
